import * as Sentry from '@sentry/react';
import { API_BASE } from './apiBase';

const SESSION_KEY = 'so_session_id';

export function getSessionId(): string {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    const match = document.cookie.match(/so_sid=([^;]+)/);
    if (match) id = match[1];
  }
  if (!id) id = crypto.randomUUID();
  localStorage.setItem(SESSION_KEY, id);
  document.cookie = `so_sid=${id}; max-age=${30 * 24 * 60 * 60}; path=/; SameSite=Lax`;
  return id;
}

export async function captureClick(): Promise<void> {
  try {
    const params = new URLSearchParams(window.location.search);
    const sessionId = getSessionId();

    const data: Record<string, string> = { sessionId };

    // UTM params
    for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']) {
      const val = params.get(key);
      if (val) data[key.replace('utm_', 'utm').replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())] = val;
    }
    // Fix camelCase: utmsource → utmSource etc
    if (params.get('utm_source')) data.utmSource = params.get('utm_source')!;
    if (params.get('utm_medium')) data.utmMedium = params.get('utm_medium')!;
    if (params.get('utm_campaign')) data.utmCampaign = params.get('utm_campaign')!;
    if (params.get('utm_content')) data.utmContent = params.get('utm_content')!;
    if (params.get('utm_term')) data.utmTerm = params.get('utm_term')!;

    // Affiliate
    if (params.get('aff')) data.affId = params.get('aff')!;
    if (params.get('sub1')) data.sub1 = params.get('sub1')!;
    if (params.get('sub2')) data.sub2 = params.get('sub2')!;
    if (params.get('sub3')) data.sub3 = params.get('sub3')!;

    // Network click IDs
    if (params.get('gclid')) data.gclid = params.get('gclid')!;
    if (params.get('fbclid')) data.fbclid = params.get('fbclid')!;
    if (params.get('ttclid')) data.ttclid = params.get('ttclid')!;
    if (params.get('msclkid')) data.msclkid = params.get('msclkid')!;

    // Referral
    if (params.get('ref')) data.refCode = params.get('ref')!;

    data.landingPage = window.location.pathname;
    data.userAgent = navigator.userAgent;

    // Only fire if we have UA params or haven't captured yet
    const hasCaptured = localStorage.getItem('so_click_captured');
    const hasUaParams = Object.keys(data).length > 3; // sessionId + landingPage + userAgent = 3
    if (!hasUaParams && hasCaptured) return;

    await fetch(`${API_BASE}/api/ua/click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    localStorage.setItem('so_click_captured', 'true');
  } catch {
    // Silent — don't block app load
  }
}

export async function attributeSession(token: string): Promise<void> {
  try {
    const sessionId = getSessionId();
    const res = await fetch(`${API_BASE}/api/ua/attribute`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ sessionId }),
    });
    // Non-2xx is a silent-attribution-loss risk (the historical /dashboard bug
    // class) — surface it instead of swallowing.
    if (!res.ok) {
      Sentry.captureMessage(`attributeSession non-OK: ${res.status}`, 'warning');
    }
  } catch (err) {
    // Never block the app, but DON'T fail invisibly — a broken attribution chain
    // must be visible in Sentry so a future silent break is caught.
    Sentry.captureException(err);
  }
}
